import { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {
  CHECK_LABELS,
  PILLAR_LABELS,
  rankActionableChecks,
  resultColor,
} from './uavsMeta';
import { generateUavsFix, applyUavsFix } from '../../services/uavsService';
import { composeServicePage } from '../../services/sitePageAuthoringService';
import {
  writeContentDraft,
  listContent,
} from '../../services/contentEditorService';
import { useTokens } from '../primitives';

// Mirrors the backend + ServicePageAuthor slugify so the duplicate pre-check
// computes the same services/{slug}.md path the server writes.
function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

// One prioritized gap. When the tenant has done-for-you implementation
// (canAutoFix, The Ryzze), it offers "Fix this for you": the AI drafts a fix
// (content_* gaps) or confirms the team handles it (structural / off-site),
// then the client applies it with one click (D30). The score updates on the
// next scheduled scan, not on apply (owner directive). Lower tiers see the
// finding plus a quiet "Done for you on The Ryzze" nudge, and can still act on
// the recommendation themselves.
function PriorityRow({ check, canAutoFix, onApplied }) {
  const t = useTokens();
  const lost = (check.pointsMax || 0) - (check.pointsEarned || 0);
  const [state, setState] = useState('idle'); // idle|loading|proposed|applying|applied|error
  const [proposal, setProposal] = useState(null);
  const [appId, setAppId] = useState(null);
  const [routed, setRouted] = useState(false);
  const [note, setNote] = useState('');

  async function runFix() {
    if (state === 'loading') return;
    setState('loading');
    setNote('');
    try {
      const { data } = await generateUavsFix(check.checkKey);
      setProposal(data?.proposal || null);
      setAppId(data?.applicationId || null);
      setRouted(Boolean(data?.routedToTeam));
      setState('proposed');
    } catch {
      setState('error');
      setNote('We could not draft that just now. Please try again.');
    }
  }

  // Routed gaps (structural / off-site) record the work order. Content gaps
  // create a real new service page: compose it through the shipped service-page
  // author, write it to the uat DRAFT (never live), then stamp the artifact
  // with the commit. Any failure leaves the artifact un-applied and tells the
  // client honestly, so nothing over-claims and no site is left half-written.
  async function apply() {
    if (!appId) return;
    setState('applying');
    try {
      if (routed) {
        await applyUavsFix(appId);
        setState('applied');
        if (onApplied) onApplied();
        return;
      }

      const serviceName = String(proposal?.title || '').trim();
      const serviceDescription = String(
        proposal?.content || proposal?.rationale || ''
      ).trim();
      if (!serviceName || !serviceDescription) throw new Error('bad seed');

      // Duplicate pre-check against the same services/{slug}.md the server writes.
      const slug = slugify(serviceName);
      try {
        const files = await listContent('uat');
        const exists = (Array.isArray(files) ? files : []).some((f) =>
          String(f?.path || '').endsWith(`/services/${slug}.md`)
        );
        if (exists) {
          setState('error');
          setNote(
            `You already have a page for "${serviceName}". Open Pages and posts to expand it.`
          );
          return;
        }
      } catch {
        /* list unavailable: fall through; the write path still guards */
      }

      const { page } = await composeServicePage({
        serviceName,
        serviceDescription,
      });
      const res = await writeContentDraft({
        path: page.path,
        content: page.markdown,
      });
      await applyUavsFix(appId, res?.commitSha);
      setState('applied');
      setNote(
        `We added a new service page to your site draft (${serviceName}). It shows on your next scan after you publish.`
      );
      if (onApplied) onApplied();
    } catch {
      // Nothing was written: leave the artifact proposed and be honest.
      setState('error');
      setNote(
        'We could not add it automatically just now. Please try again, or our team will handle it for you.'
      );
    }
  }

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: t.card,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 0.5 }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {CHECK_LABELS[check.checkKey] || check.checkKey}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {PILLAR_LABELS[check.pillarKey]} · up to{' '}
            <Box
              component="span"
              sx={{ color: resultColor(check.result, t), fontWeight: 600 }}
            >
              +{lost} {lost === 1 ? 'point' : 'points'}
            </Box>{' '}
            available
          </Typography>
        </Box>
        {canAutoFix && state !== 'applied' ? (
          <Button
            size="small"
            variant="text"
            disabled={state === 'loading' || state === 'applying'}
            onClick={runFix}
            startIcon={
              state === 'loading' ? (
                <CircularProgress size={12} />
              ) : (
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{ textTransform: 'none', color: t.purple, flexShrink: 0 }}
          >
            Fix this for you
          </Button>
        ) : !canAutoFix ? (
          <Typography
            title="We implement fixes for you on the Full Service plan."
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: 'text.disabled',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            Done for you on Full Service
          </Typography>
        ) : null}
      </Stack>

      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {check.recommendation}
      </Typography>

      {state === 'error' ? (
        <Typography
          variant="caption"
          sx={{ color: 'error.main', mt: 0.5, display: 'block' }}
        >
          {note || 'Something went wrong. Please try again.'}
        </Typography>
      ) : null}

      {state === 'applied' ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ mt: 1 }}
        >
          <CheckCircleRoundedIcon
            sx={{ fontSize: 16, color: 'success.main' }}
          />
          <Typography
            variant="caption"
            sx={{ color: 'success.main', fontWeight: 600 }}
          >
            {note ||
              (routed
                ? 'Our team has this. It shows on your next scan.'
                : 'Done. Your score updates on your next scan.')}
          </Typography>
        </Stack>
      ) : null}

      {(state === 'proposed' || state === 'applying') && proposal ? (
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            borderRadius: 2,
            bgcolor: t.purpleBg,
            border: '1px solid',
            borderColor: t.purpleBorder,
          }}
        >
          {!routed && proposal.title ? (
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
              New service page: {proposal.title}
            </Typography>
          ) : null}
          {proposal.rationale ? (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}
            >
              {proposal.rationale}
            </Typography>
          ) : null}
          {!routed && proposal.content ? (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                fontStyle: 'italic',
                mb: 1,
              }}
            >
              {proposal.content}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={state === 'applying'}
              onClick={apply}
              startIcon={
                state === 'applying' ? <CircularProgress size={12} /> : null
              }
              sx={{
                textTransform: 'none',
                bgcolor: t.purple,
                '&:hover': { opacity: 0.9 },
              }}
            >
              {routed
                ? 'Have our team apply it'
                : state === 'applying'
                  ? 'Creating the page...'
                  : 'Create this page for me'}
            </Button>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}

export default function TopPriorities({
  pillarScores,
  canAutoFix = false,
  onFixApplied,
}) {
  const t = useTokens();
  const ranked = rankActionableChecks(pillarScores).slice(0, 3);
  if (ranked.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: t.amberBg,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <ErrorOutlineIcon sx={{ color: t.amberText, fontSize: 20 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Your top {ranked.length === 1 ? 'priority' : 'priorities'}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {canAutoFix
          ? 'We can fix these for you. Your score updates on your next scan.'
          : 'Focusing here will move your score the fastest.'}
      </Typography>
      <Stack spacing={1.5}>
        {ranked.map((c) => (
          <PriorityRow
            key={c.checkKey}
            check={c}
            canAutoFix={canAutoFix}
            onApplied={onFixApplied}
          />
        ))}
      </Stack>
    </Paper>
  );
}
